//src/components/accounts/admin/AdminStubListPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import auth from '../../../config/firebase';

const AdminStubList = () => { // Removed props
  const [stubs, setStubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedStubId, setSelectedStubId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState(null);     // Keep local state
  const [success, setSuccess] = useState(null); // Keep local state
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllStubs();
  }, []);

  const fetchAllStubs = async () => {
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();
      
      const response = await fetch('/api/divesitestubs', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bounties. Please try again later.');
      }

      const data = await response.json();
      setStubs(Array.isArray(data) ? data : []);
    } catch (error) {
      setError(error.message);
      setStubs([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'primary';
      case 'claimed': return 'warning';
      case 'underreview': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const handleEdit = (stubId) => {
    navigate(`/admin/stubs/edit/${stubId}`);
  };

  const handleApproveClick = (stubId) => {
    setSelectedStubId(stubId);
    setApproveDialogOpen(true);
  };

  const handleRejectClick = (stubId) => {
    setSelectedStubId(stubId);
    setRejectDialogOpen(true);
  };

  const handleApprove = async () => {
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();
      
      // Remove the body - let the controller handle the user info from JWT
      const response = await fetch(`/api/divesitestubs/${selectedStubId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to approve stub');
      }

      setSuccess('Bounty approved and published successfully as a divesite!');
      setApproveDialogOpen(false);
      setSelectedStubId(null);
      fetchAllStubs(); // Refresh the list
    } catch (error) {
      setError(error.message);
    }
  };

  const handleReject = async () => {
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();
      
      const response = await fetch(`/api/divesitestubs/${selectedStubId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          rejectionReason,
          updatePerson: user.uid
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reject bounty');
      }

      setSuccess('Stub rejected successfully!');
      setRejectDialogOpen(false);
      setSelectedStubId(null);
      setRejectionReason('');
      fetchAllStubs(); // Refresh the list
    } catch (error) {
      setError(error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading divesite bounties to review...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Add error/success alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        All Divesite Bounties Ever... ({stubs.length})
      </Typography>
      
      {stubs.length === 0 ? (
        <Alert severity="info">No divesite bounties found.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stubs.map((stub) => (
                <TableRow key={stub._id}>
                  <TableCell>
                    {stub.uddf?.divesite?.site?.name || 'Unnamed'}
                  </TableCell>
                  <TableCell>
                    {stub.uddf?.divesite?.site?.geography?.location || 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={stub.stubInfo?.status || 'new'} 
                      color={getStatusColor(stub.stubInfo?.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {stub.stubInfo?.createPerson?.displayName || 
                     stub.stubInfo?.createPerson?.name || 
                     stub.stubInfo?.createPerson?.email || 
                     'Unknown'}
                  </TableCell>
                  <TableCell>
                    {formatDate(stub.stubInfo?.createDate || stub.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEdit(stub._id)}
                      >
                        Edit
                      </Button>
                      {(stub.stubInfo?.status === 'underreview' || stub.stubInfo?.status === 'claimed') && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleApproveClick(stub._id)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => handleRejectClick(stub._id)}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
      >
        <DialogTitle>Approve Divesite Bounty</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve this divesite bounty? It will be published to the main dive site collection and will be visible to all users.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApprove} color="success" variant="contained">
            Approve & Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
      >
        <DialogTitle>Reject Dive Site Stub</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for rejecting this divesite bounty. This will be visible to the creator.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="rejection-reason"
            label="Rejection Reason"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleReject} 
            color="error" 
            variant="contained"
            disabled={!rejectionReason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminStubList;
